import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import type { Document_File } from '@risksmart-app/web-graphql-client/derived-types';
import type { ChangeRequestPartsFragment } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  Approval_Status_Enum,
  Version_Status_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import SimpleRatingBadge from 'src/components/simple-rating-badge';
import { getReviewDueDateCutoff } from 'src/pages/policy/helpers';

type Props = {
  item: Pick<Document_File, 'NextReviewDate' | 'Status'>;
  changeRequests?: Pick<ChangeRequestPartsFragment, 'ChangeRequestStatus'>[];
};

const PolicyDocumentStatusBadge = ({ item, changeRequests }: Props) => {
  const { getByValue: getDocumentFileStatus } = useRating(
    'document_file_status'
  );

  const reviewDueSoon = useMemo(() => {
    const reviewDate = dayjs(item.NextReviewDate);
    const reviewDueDateCutoff = getReviewDueDateCutoff();

    return reviewDate.isBefore(reviewDueDateCutoff);
  }, [item.NextReviewDate]);

  const status = useMemo(() => {
    if (reviewDueSoon && item.Status === Version_Status_Enum.Published) {
      return 'review_due';
    }
    if (
      changeRequests?.some(
        (cr) => cr.ChangeRequestStatus === Approval_Status_Enum.Pending
      )
    ) {
      return 'pending_approval';
    }

    return item.Status;
  }, [reviewDueSoon, item.Status, changeRequests]);

  const rating = useMemo(() => {
    return getDocumentFileStatus(status);
  }, [status, getDocumentFileStatus]);

  return <SimpleRatingBadge rating={rating} />;
};

export default PolicyDocumentStatusBadge;
